import { useQuery } from "@apollo/client";
import { useFormik } from "formik";
import { get } from "lodash";
import Image from "next/image";
import Link from "next/link";
import { FC, useEffect } from "react";
import { toast } from "react-toastify";
import { useRecoilValue, useSetRecoilState } from "recoil";
import Button from "~/components/Button";
import Container from "~/components/Container";
import Divider from "~/components/Divider";
import Text from "~/components/Text";
import Colors from "~/constants/colors";
import { Identifiers, SectionsKey } from "~/constants/enum";
import { GUEST_CART_ID } from "~/constants/localstore";
import { useCart } from "~/data/cart";
import { GET_STORE_GQL, QUERY_GET_CMS_BLOCKS } from "~/data/home";
import { CartAtom } from "~/services/cart";
import OrderAtom from "~/services/order/order.atom";
import { UserAtom } from "~/services/user";
import styles from "~/styles/pages/ThankYou.module.scss";
import { renderSectionByKey } from "~/utils/pageTwo";
import { transFormCart } from "~/utils/transform";
import BecomeMember from "./BecomeMember";
import OrderReview from "./OrderReview";
import dynamic from "next/dynamic";

const PageNotFound: any = dynamic(() => import("~/sections/NotFound"), {
  ssr: false,
});

const ThankYou: FC = () => {
  const { createEmptyCart, getCartInfo } = useCart();
  const setCart = useSetRecoilState(CartAtom.cart);
  const currentUser = useRecoilValue(UserAtom.currentUser);
  const order = useRecoilValue(OrderAtom.order);

  // const [countdown] = useCountdown();
  const store = useQuery(GET_STORE_GQL);
  const { data: dataThankNotice } = useQuery(QUERY_GET_CMS_BLOCKS, {
    variables: {
      identifiers: Identifiers.ThankYou,
    },
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: { birthday: "" },
    onSubmit: (values) => {
      toast.success("Submit successfully");
    },
  });

  useEffect(() => {
    createEmptyCart({
      onCompleted(data, clientOptions) {
        const createEmptyCart = data?.createEmptyCart;
        if (!createEmptyCart) return;
        if (!currentUser?.token) {
          localStorage.setItem(GUEST_CART_ID, createEmptyCart);
        }
        setCart((prev) => ({
          ...prev,
          cartId: createEmptyCart as string,
        }));

        getCartInfo({
          variables: {
            cartId: createEmptyCart,
          },
          onCompleted(data) {
            setCart((prev) => ({
              ...prev,
              ...transFormCart(data),
            }));
          },
        });
      },
    });
  }, []);

  const handleCopyLink = (url: any) => {
    navigator.clipboard.writeText(url);
    toast.success("Copy link successfully");
    // setTimeout(() => {
    //   setCopy(false);
    // }, 2000);
  };

  if (!order) {
    return (
      <Container
        rootClassName="py-10 lg:py-16 lg:pt-3"
        className="flex flex-col"
      >
        <PageNotFound />
      </Container>
    );
  }

  return (
    <Container rootClassName="py-10 lg:py-16 lg:pt-3" className="flex flex-col">
      <div className="flex flex-col-reverse gap-5 lg:flex-row">
        <div className="flex flex-col gap-6 lg:gap-10">
          <Image
            className="mx-auto"
            src="/images/delivery.png"
            alt="Delivery guy"
            width={213}
            height={204}
            priority={false}
          />
          <div className="flex flex-col gap-3">
            <Text className="text-center text-grey-900 text-xl lg:text-3xl font-semibold">
              Thank You
            </Text>
            <Text type="body-1" className="text-justify md:text-center">
              <div
                dangerouslySetInnerHTML={{
                  __html: get(
                    dataThankNotice,
                    "cmsBlocks.items[0].content",
                    `Thank you for choosing! Your support is truly appreciated. We hope
              you enjoy your purchase!`,
                  ),
                }}
              ></div>
            </Text>
            {/* order_number is empty when checkout recovered an order whose place-order response was lost */}
            {order?.order_number ? (
              <Text type="title-3" className="text-center">
                Your order{" "}
                <span className="text-primary">#{order?.order_number}</span> is
                confirmed!
              </Text>
            ) : (
              <Text type="title-3" className="text-center">
                Your order is confirmed! A confirmation email will be sent to{" "}
                {order?.email || "your email"}.
              </Text>
            )}
          </div>

          {!currentUser?.token && !!order?.order_number && (
            <div className={styles.box}>
              {" "}
              <BecomeMember
                order_number={order?.order_number as string}
                email={order?.email}
              />
            </div>
          )}
          <div className={styles.box}>
            <Text type="title-1" className="text-primary">
              Customer Information
            </Text>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="flex flex-col gap-3">
                <Text type="label-2" className="text-grey-900">
                  Contact Information
                </Text>
                {order?.email && (
                  <Text className="text-grey-500" type="body-2">
                    {order.email}
                  </Text>
                )}
                {order?.shipping_addresses?.[0]?.telephone && (
                  <Text className="text-grey-500" type="body-2">
                    {order.shipping_addresses?.[0]?.telephone}
                  </Text>
                )}
              </div>
              <div className="flex flex-col lg:items-end gap-3">
                <Text type="label-2" className="text-grey-900">
                  Payment Method
                </Text>
                <Text className="text-grey-500" type="body-2">
                  {order?.selected_payment_method?.title}
                </Text>
              </div>

              <div className="flex flex-col ">
                <Text type="label-2" className="text-grey-900">
                  Shipping Address
                </Text>
                <Text className="text-grey-500" type="body-2">
                  {order?.shipping_addresses?.[0]?.firstname}{" "}
                  {order?.shipping_addresses?.[0]?.lastname}
                </Text>
                <Text className="text-grey-500" type="body-2">
                  {[
                    order?.shipping_addresses?.[0]?.street,
                    order?.shipping_addresses?.[0]?.street1,
                  ]
                    ?.filter((item: any) => !!item)
                    .join(", ")}
                </Text>
                <Text className="text-grey-500" type="body-2">
                  {[
                    order?.shipping_addresses?.[0]?.city,
                    order?.shipping_addresses?.[0]?.postcode,
                  ]
                    ?.filter((item: any) => !!item)
                    .join(", ")}
                </Text>
                <Text className="text-grey-500" type="body-2">
                  {[
                    order?.shipping_addresses?.[0]?.country?.label,
                    order?.shipping_addresses?.[0]?.region?.label,
                  ]
                    ?.filter((item: any) => !!item)
                    .join(", ")}
                </Text>
                <Text className="text-grey-500" type="body-2">
                  T: {order?.shipping_addresses?.[0]?.telephone}
                </Text>
              </div>
              <div className="flex flex-col lg:items-end">
                <Text type="label-2" className="text-grey-900">
                  Billing Address
                </Text>
                <Text className="text-grey-500" type="body-2">
                  {order?.billing_address?.firstname}{" "}
                  {order?.billing_address?.lastname}
                </Text>
                <Text className="text-grey-500" type="body-2">
                  {[
                    order?.billing_address?.street,
                    order?.billing_address?.street1,
                  ]
                    ?.filter((item: any) => !!item)
                    .join(", ")}
                </Text>
                <Text className="text-grey-500" type="body-2">
                  {[
                    order?.billing_address?.city,
                    order?.billing_address?.postcode,
                  ]
                    ?.filter((item: any) => !!item)
                    .join(", ")}
                </Text>
                <Text className="text-grey-500" type="body-2">
                  {[
                    order?.billing_address?.country?.label,
                    order?.billing_address?.region?.label,
                  ]
                    ?.filter((item) => !!item)
                    .join(", ")}
                </Text>
                <Text className="text-grey-500" type="body-2">
                  T: {order?.billing_address?.telephone}
                </Text>
              </div>

              <div className="flex flex-col gap-3">
                <Text type="label-2" className="text-grey-900">
                  Shipping Method
                </Text>
                <Text className="text-grey-500" type="body-2">
                  {order?.selected_shipping_method?.carrier_title}
                </Text>
              </div>
              <div className="flex flex-col gap-3 lg:items-end">
                <Text type="label-2" className="text-grey-900">
                  Customer Notes
                </Text>
                <Text className="text-grey-500" type="body-2">
                  {order?.customer_notes}
                </Text>
              </div>
            </div>
          </div>
          {!!order?.order_number && (
            <Link
              className="w-full"
              href={
                process.env.NEXT_PUBLIC_URL +
                "/tracking-order?id=" +
                order?.order_number +
                "&email=" +
                order?.email
              }
              target="_blank"
            >
              <Button className="w-full">Track Your Order</Button>
            </Link>
          )}
        </div>

        <Divider
          className="none lg:block"
          dividerClassName="h-auto border-t-0 border-l"
          dividerColor={Colors.GREY_100}
        />
        <div className="flex flex-col gap-5 lg:w-1/3 shrink-0">
          <Divider dividerClassName="border-dashed" />
          <OrderReview order={order} />
        </div>
      </div>
    </Container>
  );
};

export default ThankYou;
